import Tracking from '@vromo/analytics';
import React, { CSSProperties, memo, useCallback, useEffect, useState } from 'react';

import { LocalStorage } from '@vromo/models';

import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Input from '@vromo/ui/components/Input';
import Table from '@vromo/ui/components/Table';
import theme from '@vromo/ui/components/theme';
import Tooltip from '@vromo/ui/components/Tooltip';

import sortBy from 'lodash/sortBy';

import { useStore } from '@vromo/mobx-react';
import { RootStore } from 'dashboard/src/stores/RootStore';
import { observer } from 'mobx-react-lite';
import { Job } from 'models';
import { useTranslation } from 'react-i18next';
import { downloadCSV } from '../helpers';
import { ExtendedVehicle } from '../ReportRoot.lazy';
import {
    CellCenter,
    CellRight,
    HeaderRight,
    RateInput,
    RatesWrapper,
    Segment,
    SegmentRow,
    SegmentTitle
} from './Components';
import createCSVForDrivers from './oldCreateCSVForDrivers';
import styles from './driverReport.style';
import { getDriverData } from './helpers';

export type Rate = {
    fixed: number;
    perCompletedJob: number;
    perCancelled: number;
    perKmJob: number;
    perKmEmpty: number;
};

interface Props {
    vehicleSummary: ExtendedVehicle[];
    jobs: Job[];
    csvTitle: string;
}

export type DriverReportViewData = {
    id: string;
    name: string;
    group: string;
    availableHours: string;
    timeOnJob: string;
    totalTravel: string;
    onJobTravel: string;
    offJobTravel: string;
    avgRating: string;
    payEstimate: string;
    costPerJob: string;
    jobsCompleted: string;
    jobsCancelled: string;
    jobsReoffered: string;
    phone: string;
    email: string;
    external: string;
};

interface RateInputSectionProps {
    label: string;
    id: string;
    testId: string;
    value: number;
    onChange?: JSX.IntrinsicElements['input']['onChange'];
}

const RateInputSection = memo(({ label, id, testId, value, onChange }: RateInputSectionProps) => (
    <RateInput>
        <label>{label}</label>
        <Input
            {...{ id, value, onChange }}
            data-testid={testId}
            style={styles.numberInput}
            type="number"
            step="0.01"
        />
    </RateInput>
));

const TableHeaders = memo(
    ({
        distanceUnit,
        getLocale
    }: {
        distanceUnit: string;
        getLocale: (key: string, params?: { unit?: string }) => string;
    }) => {
        return (
            <thead>
                <tr>
                    <th>{getLocale('name')}</th>
                    <th>{getLocale('group')}</th>
                    <th style={styles.infoTitle as CSSProperties}>
                        {getLocale('availableHours')}
                        <Tooltip position="bottom" label={getLocale('availableHoursTooltip')}>
                            <div style={styles.infoIconContainer}>
                                <FontAwesome
                                    icon="info-circle"
                                    weight="duotone"
                                    colour={theme.lightGrey}
                                />
                            </div>
                        </Tooltip>
                    </th>
                    <HeaderRight>{getLocale('timeOnJob')}</HeaderRight>
                    <HeaderRight>{getLocale('jobsCompleted')}</HeaderRight>
                    <HeaderRight>{getLocale('jobsCancelled')}</HeaderRight>
                    <HeaderRight>{getLocale('jobsReOffered')}</HeaderRight>
                    <HeaderRight>{getLocale('totalDistance', { unit: distanceUnit })}</HeaderRight>
                    <HeaderRight>{getLocale('onJobDistance', { unit: distanceUnit })}</HeaderRight>
                    <HeaderRight>{getLocale('offJobDistance', { unit: distanceUnit })}</HeaderRight>
                    <HeaderRight>{getLocale('averageRating')}</HeaderRight>
                    <HeaderRight>{getLocale('payEstimate')}</HeaderRight>
                    <HeaderRight>{getLocale('costPerJob')}</HeaderRight>
                </tr>
            </thead>
        );
    }
);

const DriverRow = memo(
    ({
        id,
        name,
        group,
        availableHours,
        timeOnJob,
        totalTravel,
        onJobTravel,
        offJobTravel,
        avgRating,
        payEstimate,
        costPerJob,
        jobsCompleted,
        jobsCancelled,
        jobsReoffered
    }: DriverReportViewData) => {
        return (
            <tr key={id}>
                <td>{name}</td>
                <td>{group}</td>
                <CellCenter>{availableHours}</CellCenter>
                <CellRight>{timeOnJob}</CellRight>
                <CellRight>{jobsCompleted}</CellRight>
                <CellRight>{jobsCancelled}</CellRight>
                <CellRight>{jobsReoffered}</CellRight>
                <CellRight>{totalTravel}</CellRight>
                <CellRight>{onJobTravel}</CellRight>
                <CellRight>{offJobTravel}</CellRight>
                <CellRight>{avgRating}</CellRight>
                <CellRight>{payEstimate}</CellRight>
                <CellRight>{costPerJob}</CellRight>
            </tr>
        );
    }
);

const DriverReport = observer<Props>(({ vehicleSummary, jobs, csvTitle }) => {
    const { t } = useTranslation();
    const getLocale = useCallback(
        (key: string, params?: { unit?: string }) => t(`driverReport.${key}`, params),
        [t]
    );

    const { viewStore } = useStore<RootStore>('rootStore');
    const [rates, setRates] = useState<Rate>(
        LocalStorage.driverReportPayRates || {
            fixed: 0.0,
            perCompletedJob: 0.0,
            perCancelled: 0.0,
            perKmJob: 0.0,
            perKmEmpty: 0.0
        }
    );

    useEffect(() => {
        LocalStorage.driverReportPayRates = rates;
    }, [rates]);

    const updateRate = useCallback((rateField: string, val: string) => {
        const valueAsFloat = parseFloat(val);
        setRates((prevRates) => ({ ...prevRates, [rateField]: valueAsFloat }));
    }, []);

    const validRates = () => Object.values(rates).every((rateValue: number) => !isNaN(rateValue));

    const driverData = vehicleSummary.reduce((accum, currentVehicle) => {
        accum[currentVehicle.id!.toString()] = getDriverData(
            currentVehicle,
            jobs,
            validRates() ? rates : undefined
        );
        return accum;
    }, {} as { [key: string]: DriverReportViewData });

    const createDriverCsv = () => {
        Tracking.track('Driver CSV Downloaded');
        downloadCSV(createCSVForDrivers(driverData), csvTitle);
    };

    const distanceUnit = viewStore.imperialUnits ? 'mi' : 'km';
    const unitParam = {
        unit: viewStore.imperialUnits ? 'mile' : 'Km'
    };

    const onInputChange = useCallback((e) => updateRate(e.target.id, e.target.value), [updateRate]);

    return (
        <div style={styles.container}>
            <SegmentRow>
                <Segment>
                    <SegmentTitle>
                        <h4>{getLocale('setPayRates')}</h4>
                    </SegmentTitle>
                    <RatesWrapper>
                        <RateInputSection
                            label={getLocale('fixed')}
                            id={'fixed'}
                            testId={'driverreport-rates-fixed'}
                            value={rates.fixed}
                            onChange={onInputChange}
                        />
                        <RateInputSection
                            label={getLocale('perCompleteJob')}
                            id={'perCompletedJob'}
                            testId={'driverreport-rates-perCompletedJob'}
                            value={rates.perCompletedJob}
                            onChange={onInputChange}
                        />
                        <RateInputSection
                            label={getLocale('perCancelled')}
                            id={'perCancelled'}
                            testId={'driverreport-rates-perCancelled'}
                            value={rates.perCancelled}
                            onChange={onInputChange}
                        />
                        <RateInputSection
                            label={getLocale('perKmJob', unitParam)}
                            id={'perKmJob'}
                            testId={'driverreport-rates-perKmJob'}
                            value={rates.perKmJob}
                            onChange={onInputChange}
                        />
                        <RateInputSection
                            label={getLocale('perKmEmpty', unitParam)}
                            id={'perKmEmpty'}
                            testId={'driverreport-rates-perKmEmpty'}
                            value={rates.perKmEmpty}
                            onChange={onInputChange}
                        />
                    </RatesWrapper>
                    <SegmentTitle>
                        <h4>{getLocale('driverDetails')}</h4>
                        <Button onClick={createDriverCsv}>{getLocale('exportCSV')}</Button>
                    </SegmentTitle>
                    <div style={styles.tableContainer}>
                        <Table>
                            <TableHeaders {...{ distanceUnit, getLocale }} />
                            <tbody>
                                {sortBy(
                                    vehicleSummary,
                                    (vehicle) => driverData[vehicle.id!]!.name
                                ).map((vehicle) => (
                                    <DriverRow {...driverData[vehicle.id!]!} />
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Segment>
            </SegmentRow>
        </div>
    );
});

export default DriverReport;
